import React, { useEffect, useState } from "react";
import {
  Dialog,
  Stack,
  Typography,
  Link,
  useTheme,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useForm } from "react-hook-form";
import { LoadingButton } from "@mui/lab";

import { useDialog, useRequestState, useResponsive } from "@app/hooks";
import { FormProvider, RHFTextField, useSnackbar, LocationSelector } from "@app/components";
import { Strings } from "@app/constants";
import { useDispatch, useSelector } from "react-redux";
import { loginWithOtpSchema } from "../validators";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { login, resetLoginState } from "../slice";
import { selectLoginState } from "../selectors";
import { selectIsAuthenticated } from "@app/modules/common";
import { sendOtpUsingWidget } from "@app/components/msg91/msg91Otp";
import { request } from "@app/services";
import { isRegistered } from "../endpoints";

declare global {
  interface Window {
    initSendOTP?: (config: any) => void;
    sendOtp?: (mobile: string, success: (res: any) => void, err: (e: any) => void) => void;
    verifyOtp?: any;
    retryOtp?: any;
  }
}

interface LoginDialogProps {
  open: boolean;
  onClose: () => void;
  onSignUpClick?: () => void;
  onClickOtpVerificationsDialog?: () => void;
  setLoginFormData?: (form: any) => void;
}

const textStyle = (size: number, weight = 400, color?: string) => ({
  fontFamily: "Work Sans",
  fontWeight: weight,
  fontSize: size,
  ...(color && { color }),
});

const FormField = ({
  name,
  label,
  placeholder,
  isMobile,
}: {
  name: string;
  label: string;
  placeholder: string;
  isMobile: boolean;
}) => {
  const theme = useTheme();
  return (
    <Stack width={isMobile ? "100%" : "82%"}>
      <Typography sx={textStyle(14, 500, theme.palette.grey[700])} mb={0.5}>
        {label}
      </Typography>
      <RHFTextField
        name={name}
        placeholder={placeholder}
        variant="outlined"
        fullWidth
        sx={{
          "& .MuiOutlinedInput-root fieldset": { border: "none" },
          backgroundColor: theme.palette.grey[200],
          borderRadius: 1,
        }}
      />
    </Stack>
  );
};

const LoginDialog = ({
  open,
  onClose,
  onSignUpClick,
  onClickOtpVerificationsDialog,
  setLoginFormData,
}: LoginDialogProps) => {
  const isMobile = useResponsive("down", "md");
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const methods = useForm<any>({
    resolver: yupResolver(loginWithOtpSchema),
    defaultValues: {
      phoneNumber: "",
      email: "",
      state: "",
      city: "",
      zipCode: "",
    },
  });
  const dispatch = useDispatch();
  const loginState = useSelector(selectLoginState);
  const [isFetchingLocation, setIsFetchingLocation] = useState(false);
  const [showLocation, setShowLocation] = useState(false);
  const [isCheckingPhone, setIsCheckingPhone] = useState(false);
  const phoneValue = methods.watch("phoneNumber");

  const triggerLocationDetection = () => {
    const hasExistingLocation =
      Boolean(methods.getValues("state")) &&
      Boolean(methods.getValues("city")) &&
      Boolean(methods.getValues("zipCode"));

    if (hasExistingLocation) return;

    if (!navigator.geolocation) {
      return;
    }

    setIsFetchingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`
          );
          if (!response.ok) throw new Error("Failed to fetch location details");
          const locationData = await response.json();

          const state = locationData?.address?.state || "";
          const city =
            locationData?.address?.city ||
            locationData?.address?.town ||
            locationData?.address?.village ||
            locationData?.address?.municipality ||
            "";
          const zipCode = locationData?.address?.postcode || "";

          if (state) methods.setValue("state", state, { shouldValidate: true });
          if (city) methods.setValue("city", city, { shouldValidate: true });
          if (zipCode) methods.setValue("zipCode", zipCode, { shouldValidate: true });

        } catch (err) {
          enqueueSnackbar("Unable to fetch location details.", { variant: "error" });
        } finally {
          setIsFetchingLocation(false);
        }
      },
      (error) => {
        setIsFetchingLocation(false);
      }
    );
  };

  useEffect(() => {
    if (!open) {
      setShowLocation(false);
      return;
    }

    const checkPhoneRegistration = async () => {
      const cleaned = (phoneValue || "").replace(/\D/g, "");
      const formattedNumber =
        cleaned.length === 12 && cleaned.startsWith("91")
          ? `+${cleaned}`
          : cleaned.length === 10
            ? `+91${cleaned}`
            : null;

      if (formattedNumber) {
        setIsCheckingPhone(true);
        try {
          const response: any = await request.post(isRegistered, { phoneNumber: formattedNumber });

          const hasLocation = response?.isLocation ?? response?.data?.isLocation;

          if (hasLocation) {
            setShowLocation(false);
          } else {
            setShowLocation(true);
            triggerLocationDetection();
          }
        } catch (error) {
          setShowLocation(true);
          triggerLocationDetection();
        } finally {
          setIsCheckingPhone(false);
        }
      } else {
        setShowLocation(false);
      }
    };

    const timeoutId = setTimeout(() => {
      checkPhoneRegistration();
    }, 300);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneValue, open]);


  const onSubmit = (form: any) => {
    (async () => {
      try {
        const mobile = form.phoneNumber || form.phone || "";
        await sendOtpUsingWidget(mobile);
        enqueueSnackbar("OTP sent via widget!", { variant: "success" });
        setLoginFormData?.(form);
        onClickOtpVerificationsDialog?.();
        dispatch(login(form));
      } catch (err) {
        // widget not available or errored -> fallback to existing server-side flow
        dispatch(login(form));
      }
    })();
  };

  // MSG91 script is loaded at application root by `Msg91Provider`.
  // This component uses `window.sendOtp` if available; otherwise it falls back to
  // the existing `login` dispatch.



  useEffect(() => {
    if (loginState?.status === 2 && loginState?.data) {
      enqueueSnackbar("OTP sent successfully!", { variant: "success" });
      setLoginFormData?.(methods.getValues());
      onClickOtpVerificationsDialog?.();
    } else if (loginState?.status === 3) {
      enqueueSnackbar(loginState?.error?.message?.[0] || "Login failed", {
        variant: "error",
      });
    }
  }, [loginState]);

  const isAuthenticated = useSelector(selectIsAuthenticated);

  useEffect(() => {
    if (open) {
      dispatch(resetLoginState());
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (isAuthenticated) {
          onClose();
        }
      }}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          width: isMobile ? "100%" : "55%",
          maxWidth: isMobile ? "100%" : "90%",
          borderRadius: isMobile ? 0 : 3,
          p: isMobile ? 2 : 0,
        },
      }}
    >
      <Stack
        sx={{
          maxHeight: "100vh",
          overflowY: "auto",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <Stack
          spacing={3}
          sx={{ position: "relative", p: isMobile ? 2 : 4, px: 4 }}
        >
          {/* <IconButton
            onClick={onClose}
            sx={{ position: "absolute", top: 8, right: 8 }}
          >
            <Icon
              icon="material-symbols-light:close"
              width={24}
              height={24}
              color={theme.palette.common.black}
            />
          </IconButton> */}

          <Stack spacing={1} alignItems="center">
            <Typography sx={textStyle(isMobile ? 22 : 28, 700)}>
              {Strings.authPopup.logIn}
            </Typography>
            <Typography
              textAlign="center"
              sx={textStyle(isMobile ? 14 : 16, 400, theme.palette.grey[800])}
            >
              {Strings.authPopup.welcomeBackLogin}
            </Typography>
          </Stack>

          <FormProvider methods={methods}>
            <Stack spacing={2} alignItems="center">
              <FormField
                name="phoneNumber"
                label={`${Strings.authPopup.phoneNumber}*`}
                placeholder={Strings.authPopup.enterPhoneNumber}
                isMobile={isMobile}
              />
              <FormField
                name="email"
                label={Strings.authPopup.email}
                placeholder={Strings.authPopup.enterEmail}
                isMobile={isMobile}
              />
              <Stack width={isMobile ? "100%" : "82%"}>
                {showLocation && (
                  isFetchingLocation ? (
                    <Stack alignItems="center" justifyContent="center" py={2}>
                      <CircularProgress size={30} />
                      <Typography variant="body2" mt={1} color={theme.palette.text.secondary}>
                        Detecting location...
                      </Typography>
                    </Stack>
                  ) : (
                    <LocationSelector />
                  )
                )}
              </Stack>

              <LoadingButton
                onClick={methods.handleSubmit(onSubmit)}
                loading={isCheckingPhone}
                variant="contained"
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  borderRadius: "8px",
                  width: isMobile ? "100%" : "65%",
                  py: 1.5,
                  mt: 2,
                }}
              >
                {Strings.button.continue}
              </LoadingButton>
            </Stack>
          </FormProvider>

          {onSignUpClick && (
            <Stack alignItems="center">
              <Typography
                sx={textStyle(isMobile ? 13 : 15, 500, theme.palette.grey[700])}
              >
                {Strings.auth.doNotHave}
                <Link
                  href="#"
                  underline="hover"
                  fontFamily="Work Sans"
                  fontWeight={500}
                  ml={1}
                  onClick={onSignUpClick}
                >
                  {Strings.auth.signUp}
                </Link>
              </Typography>
            </Stack>
          )}

          <Typography
            textAlign="center"
            sx={[
              textStyle(isMobile ? 11 : 13, 500, theme.palette.grey[700]),
              { px: 4 },
            ]}
          >
            {Strings.authPopup.byContinuingYou}
          </Typography>
        </Stack>
      </Stack>
    </Dialog>
  );
};

export { LoginDialog };
